import { useState, lazy, Suspense } from "react";
import { useViewportKeyboard } from "./hooks/v2/useViewportKeyboard";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "./components/ui/toaster";
import { TooltipProvider } from "./components/ui/tooltip";
import { Route, Switch } from "wouter";
import { navigate } from "wouter/use-browser-location";
import { PageLoader } from "./components/ui/PageLoader";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { ThemePreferenceProvider } from "./contexts/ThemePreferenceContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { EvalRunProvider } from "./context/EvalRunContext";
import { SandboxRunProvider } from "./context/SandboxRunContext";
import { ConversationProvider } from "./contexts/v2/ConversationContext";
import { OverviewBentoExpandProvider } from "./contexts/v2/OverviewBentoExpandContext";
import { UserOrgsProvider, useUserOrgs } from "./contexts/v2/UserOrgsContext";
import { ConversationShell } from "./components/v2/conversation/ConversationShell";
import { RequireActiveProject } from "./components/v2/layout/RequireActiveProject";

// Lazy load components
const EvalAdminShell = lazy(() => import("./pages/admin/eval/EvalAdminShell"));
const EvalRunTriggerPage = lazy(() => import("./pages/admin/eval/EvalRunTriggerPage"));
const EvalProgressPage = lazy(() => import("./pages/admin/eval/EvalProgressPage"));
const EvalTranscriptPage = lazy(() => import("./pages/admin/eval/EvalTranscriptPage"));
const TranscriptPage = lazy(() => import("./pages/admin/eval/TranscriptPage"));
const SandboxAdminShell = lazy(() => import("./pages/admin/sandbox/SandboxAdminShell"));
const SandboxConfigPage = lazy(() => import("./pages/admin/sandbox/SandboxConfigPage"));
const SandboxTestPage = lazy(() => import("./pages/admin/sandbox/SandboxTestPage"));
const SandboxAnalysePage = lazy(() => import("./pages/admin/sandbox/SandboxAnalysePage"));
const AdminHubPage = lazy(() => import("./pages/admin/AdminHubPage"));
const ProjectsPage = lazy(() => import("./pages/ProjectsPage"));
const CreateProjectPage = lazy(() => import("./pages/CreateProjectPage"));
const ProjectBriefingPage = lazy(() => import("./pages/ProjectBriefingPage"));
const ProjectConversationPage = lazy(() => import("./pages/ProjectConversationPage"));
const HomePageV2 = lazy(() => import("./pages/v2/HomePageV2"));
const SettingsPage = lazy(() => import("./pages/v2/SettingsPage"));
const ProjectOverviewPage = lazy(() => import("./pages/v2/ProjectOverviewPage"));
const ProjectScopingPage = lazy(() => import("./pages/v2/ProjectScopingPage"));
const ProjectResearchPage = lazy(() => import("./pages/v2/ProjectResearchPage"));
const ProjectParticipantsPage = lazy(() => import("./pages/v2/ProjectParticipantsPage"));
const ProjectInterviewsPage = lazy(() => import("./pages/v2/ProjectInterviewsPage"));
const ProjectMeetingsPage = lazy(() => import("./pages/v2/ProjectMeetingsPage"));
const ProjectInsightsPage = lazy(() => import("./pages/v2/ProjectInsightsPage"));
const ProjectRecommendationsPage = lazy(() => import("./pages/v2/ProjectRecommendationsPage"));
const ProjectChatPage = lazy(() => import("./pages/v2/ProjectChatPage"));
const BetDetailPage = lazy(() => import("./pages/v2/BetDetailPage"));
const ChatThreadPage = lazy(() => import("./pages/v2/ChatThreadPage"));
const ChatPageV2 = lazy(() => import("./pages/v2/ChatPageV2"));
const InterviewPage = lazy(() => import("./pages/InterviewPage"));
const InterviewLaunchPage = lazy(() => import("./pages/InterviewLaunchPage"));
const DemoLandingPage = lazy(() => import("./pages/DemoLandingPage"));
const DemoInvitesPage = lazy(() => import("./pages/admin/DemoInvitesPage"));
const DemoBuilderPage = lazy(() => import("./pages/admin/DemoBuilderPage"));

// Error surfaces (auth-aware catch-all 404 + the /error/:code route)
const NotFoundPage = lazy(() => import("./pages/v2/NotFoundPage"));
const ErrorPage = lazy(() => import("./pages/v2/ErrorPage"));

// Lazy load authentication pages (named exports)
const AuthPage = lazy(() => import("./pages/AuthPage").then(m => ({ default: m.AuthPage })));
const AuthSuccess = lazy(() => import("./pages/AuthSuccess").then(m => ({ default: m.AuthSuccess })));
const AuthError = lazy(() => import("./pages/AuthError").then(m => ({ default: m.AuthError })));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage").then(m => ({ default: m.ResetPasswordPage })));
const MagicLoginPage = lazy(() => import("./pages/MagicLoginPage").then(m => ({ default: m.MagicLoginPage })));
const DesktopAuthSuccess = lazy(() => import("./pages/DesktopAuthSuccess"));

type ActiveTab = 'assistant' | 'goals' | 'transcript';

/**
 * Single global lifecycle + conversation bridge for all v2 routes (α3).
 * The outer lifecycle provider survives cross-project navigation; a keyed inner
 * host resets legacy `useConversation` state when the surface shortId changes.
 */
function V2ConversationRoot({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const userId = user != null ? String(user.id) : "anonymous";
  // Active org drives conversation tenancy: when it changes, the lifecycle tears
  // down so a stale conversation id can't cross orgs (data isolation).
  const { currentOrgId } = useUserOrgs();
  return (
    <ConversationProvider userId={userId} orgId={currentOrgId}>
      <OverviewBentoExpandProvider>{children}</OverviewBentoExpandProvider>
    </ConversationProvider>
  );
}

// App content wrapper. ANA-16: no client-side token refresh — the Supabase session
// lives in httpOnly sb-* cookies and is rotated server-side by requireUnifiedAuth.
// Routing is plain browser paths (no #) — wouter's default useBrowserLocation;
// the Express catch-all serves index.html for any non-API path (SPA fallback).
function AppContent() {
  useViewportKeyboard();
  return (
    <>
      <Suspense fallback={<PageLoader />}>
          <ErrorBoundary>
          <UserOrgsProvider>
          <EvalRunProvider>
          <SandboxRunProvider>
          <V2ConversationRoot>
          <div className="anaka-app bg-dot-grid">
          <Switch>
          {/* ANA-955 / ANA-43 Phase C: the Meetings v1 UI (the /dashboard meeting
              landing, /meeting/:id, /prepare/:id, /meetings*) was removed with the
              legacy /api/v1/meetings REST surface it read from. */}

          {/* Authentication Routes */}
          <Route path="/demo/:token">
            {({ token }) => <DemoLandingPage token={token} />}
          </Route>
          <Route path="/admin/demo-invites">
            <ProtectedRoute>
              <DemoInvitesPage />
            </ProtectedRoute>
          </Route>
          <Route path="/admin/demos">
            <ProtectedRoute>
              <DemoBuilderPage />
            </ProtectedRoute>
          </Route>
          <Route path="/interview/:token">
            {({ token }) => <InterviewLaunchPage token={token} />}
          </Route>

          {/* Authentication Routes */}
          <Route path="/login">
            <AuthPage />
          </Route>
          
          <Route path="/auth/success">
            <AuthSuccess />
          </Route>
          
          <Route path="/auth/error">
            <AuthError />
          </Route>
          
          <Route path="/auth/reset-password">
            <ResetPasswordPage />
          </Route>
          
          <Route path="/auth/magic-login">
            <MagicLoginPage />
          </Route>
          
          <Route path="/auth/desktop-success">
            <DesktopAuthSuccess />
          </Route>
          
          {/* Error surfaces — auth-aware, not behind ProtectedRoute.
              /error/:code maps an HTTP status to its calm-voice scenario
              (e.g. /error/403, /error/503), the explicit target a guard
              or failed data load can redirect to. */}
          <Route path="/error/:code">
            {({ code }) => <ErrorPage code={code} />}
          </Route>
          <Route path="/error">
            <ErrorPage />
          </Route>

          {/* User Settings (spec 033) — /settings for ALL authenticated users
              (not admin-gated; no sub-paths). */}
          <Route path="/settings">
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          </Route>

          {/* Admin Hub (spec 030) — /admin is the config-driven landing; the eval
              runner re-homes to /admin/evals so the hub owns /admin. */}
          <Route path="/admin">
            <ProtectedRoute>
              <AdminHubPage />
            </ProtectedRoute>
          </Route>
          <Route path="/admin/evals">
            <ProtectedRoute>
              <EvalAdminShell><EvalRunTriggerPage /></EvalAdminShell>
            </ProtectedRoute>
          </Route>
          <Route path="/admin/comparison/:id/progress">
            {(params) => (
              <ProtectedRoute>
                <EvalAdminShell><EvalProgressPage /></EvalAdminShell>
              </ProtectedRoute>
            )}
          </Route>
          <Route path="/admin/comparison/:id/case/:caseId">
            {(params) => (
              <ProtectedRoute>
                <EvalAdminShell><TranscriptPage /></EvalAdminShell>
              </ProtectedRoute>
            )}
          </Route>
          <Route path="/admin/comparison/:id">
            {(params) => (
              <ProtectedRoute>
                <EvalAdminShell><EvalTranscriptPage /></EvalAdminShell>
              </ProtectedRoute>
            )}
          </Route>

          {/* Admin Prompt Sandbox — sibling surface to the Eval Suite (spec 027).
              Shares the singleton SandboxRunProvider (mounted above) so the run
              phase set on Run persists across Config → Test → Analyse. */}
          <Route path="/admin/sandbox">
            <ProtectedRoute>
              <SandboxAdminShell><SandboxConfigPage /></SandboxAdminShell>
            </ProtectedRoute>
          </Route>
          <Route path="/admin/sandbox/test">
            <ProtectedRoute>
              <SandboxAdminShell><SandboxTestPage /></SandboxAdminShell>
            </ProtectedRoute>
          </Route>
          <Route path="/admin/sandbox/analyse">
            <ProtectedRoute>
              <SandboxAdminShell><SandboxAnalysePage /></SandboxAdminShell>
            </ProtectedRoute>
          </Route>

          {/* Project Routes */}
          <Route path="/projects/new">
            <ProtectedRoute>
              <CreateProjectPage />
            </ProtectedRoute>
          </Route>

          <Route path="/projects/:projectId/brief">
            {({ projectId }) => (
              <ProtectedRoute>
                <ProjectBriefingPage projectId={projectId} />
              </ProtectedRoute>
            )}
          </Route>

          {/* V2 workspace routes — V2 is the product (FEATURE_V2 flag removed) */}
          <>
              {/* Overview is the project's home for EVERY lifecycle stage:
                  ProjectOverviewPage branches internally (early-lifecycle
                  landing vs the populated OverviewLayout), so it is NOT wrapped
                  in RequireActiveProject — an early-lifecycle project lands here
                  without a redirect loop (spec 041 §R11). The other workspace
                  tabs below stay gated. */}
              <Route path="/projects/:shortId/overview">
                {({ shortId }) => (
                  <ProtectedRoute>
                    <ProjectOverviewPage shortId={shortId} />
                  </ProtectedRoute>
                )}
              </Route>

              <Route path="/projects/:shortId/scoping">
                {({ shortId }) => (
                  <ProtectedRoute>
                    <ProjectScopingPage shortId={shortId} />
                  </ProtectedRoute>
                )}
              </Route>

              <Route path="/projects/:shortId/research">
                {({ shortId }) => (
                  <ProtectedRoute>
                    <RequireActiveProject shortId={shortId}>
                      <ProjectResearchPage projectSlug={shortId} />
                    </RequireActiveProject>
                  </ProtectedRoute>
                )}
              </Route>

              <Route path="/projects/:shortId/participants">
                {({ shortId }) => (
                  <ProtectedRoute>
                    <RequireActiveProject shortId={shortId}>
                      <ProjectParticipantsPage projectSlug={shortId} />
                    </RequireActiveProject>
                  </ProtectedRoute>
                )}
              </Route>

              <Route path="/projects/:shortId/interviews">
                {({ shortId }) => (
                  <ProtectedRoute>
                    <RequireActiveProject shortId={shortId}>
                      <ProjectInterviewsPage projectSlug={shortId} />
                    </RequireActiveProject>
                  </ProtectedRoute>
                )}
              </Route>

              <Route path="/projects/:shortId/recommendations">
                {({ shortId }) => (
                  <ProtectedRoute>
                    <RequireActiveProject shortId={shortId}>
                      <ProjectRecommendationsPage projectSlug={shortId} />
                    </RequireActiveProject>
                  </ProtectedRoute>
                )}
              </Route>

              <Route path="/projects/:shortId/meetings">
                {({ shortId }) => (
                  <ProtectedRoute>
                    <RequireActiveProject shortId={shortId}>
                      <ProjectMeetingsPage projectSlug={shortId} />
                    </RequireActiveProject>
                  </ProtectedRoute>
                )}
              </Route>

              <Route path="/projects/:shortId/insights">
                {({ shortId }) => (
                  <ProtectedRoute>
                    <RequireActiveProject shortId={shortId}>
                      <ProjectInsightsPage projectSlug={shortId} />
                    </RequireActiveProject>
                  </ProtectedRoute>
                )}
              </Route>

              <Route path="/projects/:shortId/chat">
                {({ shortId }) => (
                  <ProtectedRoute>
                    <RequireActiveProject shortId={shortId}>
                      <ProjectChatPage projectSlug={shortId} />
                    </RequireActiveProject>
                  </ProtectedRoute>
                )}
              </Route>

              <Route path="/projects/:shortId/bet/:betId">
                {({ shortId, betId }) => (
                  <ProtectedRoute>
                    <RequireActiveProject shortId={shortId}>
                      <BetDetailPage shortId={shortId} betId={betId} />
                    </RequireActiveProject>
                  </ProtectedRoute>
                )}
              </Route>

              <Route path="/conversation/:shortId">
                {({ shortId }) => (
                  <ProtectedRoute>
                    <ChatThreadPage shortId={shortId} />
                  </ProtectedRoute>
                )}
              </Route>

              {/* Dedicated full-page home conversation. The global
                  <ConversationShell> renders the focused thread here; this
                  page is just the nav bar + a spacer below it. */}
              <Route path="/chat">
                <ProtectedRoute>
                  <ChatPageV2 />
                </ProtectedRoute>
              </Route>
          </>

          <Route path="/activities/:activityId/interview">
            {({ activityId }) => (
              <ProtectedRoute>
                <InterviewPage activityId={activityId} />
              </ProtectedRoute>
            )}
          </Route>
          
          <Route path="/projects/:projectId">
            {({ projectId }) => (
              <ProtectedRoute>
                <ProjectConversationPage projectId={projectId} />
              </ProtectedRoute>
            )}
          </Route>
          
          <Route path="/projects">
            <ProtectedRoute>
              <ProjectsPage />
            </ProtectedRoute>
          </Route>
          
          {/* Default Route - New Home Page */}
          <Route path="/">
            <ProtectedRoute>
              <HomePageV2 />
            </ProtectedRoute>
          </Route>
          
          {/* Catch-all Route — unknown URLs render the auth-aware 404 surface
              (signed-in → "page not found"; guest → "please sign in"). */}
          <Route>
            <NotFoundPage />
          </Route>
        </Switch>
          <ConversationShell />
          </div>
          </V2ConversationRoot>
          </SandboxRunProvider>
          </EvalRunProvider>
          </UserOrgsProvider>
          </ErrorBoundary>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemePreferenceProvider>
          <TooltipProvider>
            <AppContent />
            <Toaster />
          </TooltipProvider>
        </ThemePreferenceProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;